import type { ContextParams, EditorReadyFn, EditorSDK, GetAppManifest } from '@wix/platform-editor-sdk';

import { MembersAreaContext } from '../types/context';
import {
  initExperiments,
  initV3Experiments,
  shouldEnableMigration,
  shouldEnableMigrationToV3,
} from '../utils/experiments';
import { initializeMonitoring } from '../utils/monitoring';
import {
  currentMembersArea,
  exportsFn,
  getControllerPresets,
  membersAreaOnMSB,
  splitMembersArea,
} from './platform-app';
import type { MembersAreaOnEventFn, MembersAreaPlatformApp } from './platform-app/types';
import * as applicationState from './services/applicationState';
import { getMembersAreaContext } from './services/context';
import { migrateToV2OnTemplates } from './services/migrations/v2/v2-migration';
import { migrateToV3OnTemplates } from './services/migrations/v3/v3-migration';
import { parseStaticsUrlFromEditorScriptUrl } from './services/urls';

const editorPlatformAppMap: { [key in MembersAreaContext]: MembersAreaPlatformApp } = {
  [MembersAreaContext.V1]: currentMembersArea,
  [MembersAreaContext.V2]: membersAreaOnMSB,
  [MembersAreaContext.V3]: splitMembersArea,
};

const getEditorPlatformApp = async (editorSDK: EditorSDK, options?: ContextParams) => {
  const membersAreaContext = await getMembersAreaContext(editorSDK, options);
  return editorPlatformAppMap[membersAreaContext];
};

const editorReady: EditorReadyFn = async (editorSDK, _appToken, options) => {
  const appInstance = await editorSDK.info.getAppInstancePayload('');
  initExperiments(appInstance);
  initV3Experiments(appInstance);

  applicationState.setEditorSDK(editorSDK);
  applicationState.setStaticsBaseUrl(parseStaticsUrlFromEditorScriptUrl(options.initialAppData.editorScriptUrl));
  // @ts-expect-error - blogWriterProfilesOnly does not exist on Origin.info type in Editor platform sdk types
  applicationState.setIsBlogWriterProfilesOnly(!!options.origin.info?.blogWriterProfilesOnly);
  applicationState.setIsClassicEditor(options.origin.type === 'CLASSIC');
  applicationState.setIsResponsiveEditor(options.origin.type === 'RESPONSIVE');

  try {
    await initializeMonitoring(editorSDK, options);
  } catch (e) {}

  const membersAreaContext = await getMembersAreaContext(editorSDK, options);
  const isV1 = membersAreaContext === MembersAreaContext.V1;

  if (isV1) {
    if (await shouldEnableMigrationToV3()) {
      await migrateToV3OnTemplates(editorSDK);
    } else if (await shouldEnableMigration()) {
      await migrateToV2OnTemplates(editorSDK, options);
    }
  }

  const platformApp = await getEditorPlatformApp(editorSDK, options);
  return platformApp.editorReady(editorSDK, _appToken, options);
};

const onEvent: MembersAreaOnEventFn = async (event, editorSDK) => {
  const platformApp = await getEditorPlatformApp(editorSDK);
  return platformApp.onEvent(event, editorSDK);
};

const getAppManifest: GetAppManifest = async (options, editorSDK, contextParams) => {
  const platformApp = await getEditorPlatformApp(editorSDK);
  return platformApp.getAppManifest(options, editorSDK, contextParams);
};

export { editorReady, exportsFn as exports, getAppManifest, getControllerPresets, onEvent };
